import {useHierarchy} from '@domain/kpis'
import {findLocationEntry} from '@domain/shared'
import {
  AggregatedStoppage,
  AggregatedStoppageCodeStatistics,
  IssueGroupingKey,
  IssueSortingKey,
  StoppageCode,
  StoppageCodeStatistic,
  usePlantStoppages
} from '@domain/stoppages'
import {DataContentWrapper} from '@hconnect/common/containers'
import {formatNumber} from '@hconnect/common/utils'
import {DateRange} from '@hconnect/uikit'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {InfoOutlined, TextSnippetOutlined} from '@mui/icons-material'
import {Box, Button, Stack, Tooltip, Typography, useMediaQuery, useTheme} from '@mui/material'
import {UseQueryResult} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {isEmpty} from 'lodash'
import moment from 'moment-timezone'
import React, {FC, useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useDepartmentNameMap} from '../../hooks'
import {useDowntimePlantId} from '../../providers'
import {
  getBarTimeFormattedLabel,
  getTimeStep,
  isUnavailable,
  collectDepartmentUpmIdsFromStoppages
} from '../../utils'

import {ComboChart, ComboChartData} from './ComboChart'
import {GroupByDropDown} from './GroupByDropDown'
import {SortBySelect} from './SortByDropDown'

const getTheTitle = (legend: string, label: string, title: string, hideInfoIcon?: boolean) => {
  return (
    <>
      {title}
      {!hideInfoIcon && (
        <Tooltip
          enterTouchDelay={0}
          title={
            <Stack spacing={0.5} ml={{sm: 1}}>
              <Typography variant="caption">{legend}</Typography>
              <Typography variant="caption">{label}</Typography>
            </Stack>
          }
        >
          <InfoOutlined sx={{color: 'secondary.main', ml: 1.5}} />
        </Tooltip>
      )}
    </>
  )
}

type TopIssuesProps = {
  timeRange: DateRange<Date>
  stoppageConfig?: StoppageCode[]
  topIssuesSortingKey: IssueSortingKey
  onTopIssuesSortingKeyChange: (key: IssueSortingKey) => void
  sortedStoppages: AggregatedStoppage[]
  aggregatedStoppagesQuery: UseQueryResult<AggregatedStoppage[], AxiosError<unknown>>
  title: string
  stoppageCodeStats: AggregatedStoppageCodeStatistics
  issueCount?: number
  hideInfoIcon?: boolean
  showFramesLabelFromTimeRange?: boolean
  onClick?: () => void
  onBarClick?: (data?: string | undefined) => void
  onShowKilnStatsClick?: () => void
  topIssuesGroupingKey: IssueGroupingKey
} & (
  | {
      onTopIssuesGroupingKeyChange: (key: IssueGroupingKey) => void
      disableGroupingKey?: never
    }
  | {
      onTopIssuesGroupingKeyChange?: never
      disableGroupingKey: boolean
    }
)

export const TopIssues: FC<TopIssuesProps> = ({
  timeRange,
  stoppageConfig: StoppageConfigProp,
  topIssuesSortingKey,
  onTopIssuesSortingKeyChange,
  disableGroupingKey,
  topIssuesGroupingKey,
  onTopIssuesGroupingKeyChange,
  sortedStoppages,
  aggregatedStoppagesQuery,
  title,
  issueCount,
  hideInfoIcon,
  showFramesLabelFromTimeRange,
  onClick,
  onBarClick,
  onShowKilnStatsClick,
  stoppageCodeStats
}) => {
  const plantId = useDowntimePlantId()
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const stoppageConfigQuery = usePlantStoppages(plantId, {enabled: !StoppageConfigProp})
  const timezone = useUpmPlantTimezone(plantId)
  const {data: hierarchy} = useHierarchy()

  const stoppageConfig = StoppageConfigProp || stoppageConfigQuery.data
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('md'))

  const barLabel = t('label.hours')
  const lineLabel = t('label.noStoppages')

  const isStoppedMainEquipmentGrouping =
    topIssuesGroupingKey === IssueGroupingKey.STOPPED_MAIN_EQUIPMENT

  const departmentUpmIds = useMemo(
    () =>
      isStoppedMainEquipmentGrouping
        ? collectDepartmentUpmIdsFromStoppages(sortedStoppages, issueCount)
        : [],
    [isStoppedMainEquipmentGrouping, sortedStoppages, issueCount]
  )

  const departmentNameMap = useDepartmentNameMap({
    departmentUpmIds,
    isEnabled: isStoppedMainEquipmentGrouping
  })

  const chartData = useMemo<ComboChartData<string> | undefined>(() => {
    if (!isEmpty(aggregatedStoppagesQuery.data) && !isEmpty(stoppageConfig)) {
      const entries: AggregatedStoppage[] = sortedStoppages.slice(0, issueCount)
      const aggregatedStoppages: string[] = entries.map(({key}) => key)

      const barData: number[] = []
      const lineData: number[] = []
      entries.forEach((entry) => {
        barData.push(entry.hoursStopped)
        lineData.push(entry.stoppagesCount ?? 0)
      })
      let columns: string[][] = []
      if (topIssuesGroupingKey === IssueGroupingKey.STOPPAGE_CODE) {
        columns = aggregatedStoppages.map((aggregatedStoppage) => {
          const stoppageCode = stoppageConfig?.find(({code}) => code === aggregatedStoppage)
          const otherCode = isUnavailable(aggregatedStoppage)
          const text = otherCode ? t('label.stoppageTypeOther') : stoppageCode?.text
          return `${!otherCode ? aggregatedStoppage : ''}${
            text ? ` ${text.split('-')[0]}` : ''
          }`.split('-')
        })
      }
      if (topIssuesGroupingKey === IssueGroupingKey.STOPPED_MAIN_EQUIPMENT) {
        columns = aggregatedStoppages.map((aggregatedStoppage) => {
          if (isUnavailable(aggregatedStoppage)) return [t('label.notAvailable')]
          const departmentName = departmentNameMap.get(aggregatedStoppage)
          return [departmentName || aggregatedStoppage]
        })
      }
      if (topIssuesGroupingKey === IssueGroupingKey.MAIN_EQUIPMENT) {
        columns = aggregatedStoppages.map((aggregatedStoppage) => {
          if (isUnavailable(aggregatedStoppage)) return [t('label.notAvailable')]
          const stoppageCodeStat: StoppageCodeStatistic = stoppageCodeStats[aggregatedStoppage]
          const mainEquipment = stoppageCodeStat?.mainEquipments?.[0] as
            | Record<string, string>
            | undefined
          return mainEquipment ? Object.values(mainEquipment) : []
        })
      }
      if (topIssuesGroupingKey === IssueGroupingKey.EQUIPMENT_NUMBER) {
        columns = aggregatedStoppages.map((aggregatedStoppage) => {
          if (isUnavailable(aggregatedStoppage)) return [t('label.notAvailable')]
          const stoppageCodeStat: StoppageCodeStatistic = stoppageCodeStats[aggregatedStoppage]
          const equipment = stoppageCodeStat?.equipments?.[0] as Record<string, string> | undefined
          return equipment ? Object.values(equipment) : []
        })
      }
      if (topIssuesGroupingKey === IssueGroupingKey.TIME_INTERVAL) {
        const timeStep = getTimeStep(timeRange, timezone)
        columns = aggregatedStoppages.map((aggregatedStoppage) => {
          return [
            getBarTimeFormattedLabel({
              dateUtc: aggregatedStoppage,
              step: timeStep,
              language,
              timezone
            })
          ]
        })
      }
      const data = {
        columns,
        barData,
        lineData,
        barLabel,
        lineLabel,
        dataTestId: 'top-issues-chart',
        additionalData: aggregatedStoppages
      }
      return data
    }
  }, [
    aggregatedStoppagesQuery.data,
    sortedStoppages,
    issueCount,
    topIssuesGroupingKey,
    barLabel,
    lineLabel,
    stoppageConfig,
    t,
    stoppageCodeStats,
    timeRange,
    language,
    timezone,
    departmentNameMap
  ])

  const retry = useCallback(() => {
    if (aggregatedStoppagesQuery.error) {
      void aggregatedStoppagesQuery.refetch()
    }
    if (stoppageConfigQuery.error) {
      void stoppageConfigQuery.refetch()
    }
  }, [aggregatedStoppagesQuery, stoppageConfigQuery])

  const getStoppageTitle = useCallback(
    (column) => {
      if (topIssuesGroupingKey === IssueGroupingKey.STOPPAGE_CODE) {
        const stoppageCode = stoppageConfig?.find(({code}) => column.includes(code))
        return stoppageCode ? stoppageCode.code : ''
      } else {
        const [equipmentId] = column.split(',')
        return equipmentId
      }
    },
    [stoppageConfig, topIssuesGroupingKey]
  )
  const getStoppageDescription = useCallback(
    (column) => {
      if (topIssuesGroupingKey === IssueGroupingKey.STOPPAGE_CODE) {
        const stoppageCode = stoppageConfig?.find(({code}) => column.includes(code))
        return stoppageCode ? stoppageCode.text : ''
      } else {
        const [, equipmentText] = column.split(',')
        return equipmentText
      }
    },
    [stoppageConfig, topIssuesGroupingKey]
  )

  const showKilnStatsButton = hierarchy
    ? findLocationEntry(hierarchy, plantId)?.pxTrendMetaData?.pxTrendAvailable === true
    : false

  const getStoppageLabel = useCallback(
    (dataIndex: number, rawValue: number) => {
      if (dataIndex === 0) {
        return `${lineLabel}: ${formatNumber(rawValue, language)}`
      }
      return `${barLabel}: ${formatNumber(rawValue, language)}`
    },
    [barLabel, language, lineLabel]
  )

  const timeFramesTitle = useMemo(() => {
    if (showFramesLabelFromTimeRange) {
      const startDate = moment.utc(timeRange.from).tz(timezone)
      const endDate = moment.utc(timeRange.to).tz(timezone)
      const singleDate = startDate.isSame(endDate, 'date')
      const quickSelectionTitle = `${t('stoppageAnalysis.label.timeframe')}: ${t(
        `quickSelection.${timeRange.quickSelectionId}`
      )}`
      const customSelectionTitle = `${t('stoppageAnalysis.label.timeframe')}: ${startDate.format(
        'ddd MMM DD YYYY'
      )}${singleDate ? '' : ` - ${endDate.format('ddd MMM DD YYYY')}`}`

      return timeRange.quickSelectionId ? quickSelectionTitle : customSelectionTitle
    }
    return t('label.timeframe', {timeframe: 12})
  }, [
    showFramesLabelFromTimeRange,
    t,
    timeRange.from,
    timeRange.to,
    timeRange.quickSelectionId,
    timezone
  ])

  return (
    <CardBox data-test-id="top-issues">
      <Stack
        width={'100%'}
        direction={isSmall ? 'column' : 'row'}
        justifyContent="space-between"
        mb={4}
        gap={1}
      >
        <Box>
          <Typography variant={isSmall ? 'h4' : 'h3'} display="inline-flex" alignItems="center">
            {getTheTitle(title, t('label.unplannedStoppagesLegend'), timeFramesTitle, hideInfoIcon)}
          </Typography>
        </Box>
        <Box
          display="flex"
          gap={1.5}
          justifyContent="flex-end"
          sx={{
            flexFlow: {
              xs: 'column-reverse wrap',
              md: 'row wrap-reverse'
            }
          }}
        >
          {showKilnStatsButton && (
            <Box minWidth={224}>
              <Button
                color="secondary"
                onClick={onShowKilnStatsClick}
                startIcon={<TextSnippetOutlined />}
                sx={{width: '100%', minHeight: 56}}
                data-test-id="show-kiln-kpi-stats"
              >
                {t('actions.showDowntimeStatistics')}
              </Button>
            </Box>
          )}
          <Box display="flex" gap={1.5} sx={{flexFlow: {xs: 'column', md: 'unset'}}}>
            {!disableGroupingKey && topIssuesGroupingKey && onTopIssuesGroupingKeyChange && (
              <Box minWidth={224}>
                <GroupByDropDown
                  value={topIssuesGroupingKey}
                  onChange={onTopIssuesGroupingKeyChange}
                  data-test-id="top-issues-group"
                />
              </Box>
            )}

            {topIssuesGroupingKey !== IssueGroupingKey.TIME_INTERVAL && (
              <Box minWidth={224}>
                <SortBySelect
                  value={topIssuesSortingKey}
                  onChange={onTopIssuesSortingKeyChange}
                  data-test-id="top-issues-sort"
                />
              </Box>
            )}
          </Box>
        </Box>
      </Stack>

      <Box height={280}>
        <DataContentWrapper
          showLoader={
            aggregatedStoppagesQuery.isInitialLoading || stoppageConfigQuery.isInitialLoading
          }
          data={chartData}
          retryFunction={retry}
          isRefetching={aggregatedStoppagesQuery.isRefetching || stoppageConfigQuery.isRefetching}
          isError={aggregatedStoppagesQuery.isError || stoppageConfigQuery.isError}
          progressSx={{color: 'primary.main'}}
        >
          {chartData && (
            <ComboChart
              data={chartData}
              onClick={onClick}
              showPointerBarHover={true}
              onBarClick={onBarClick}
              getTitle={getStoppageTitle}
              getAfterTitle={getStoppageDescription}
              getStoppageLabel={getStoppageLabel}
            />
          )}
        </DataContentWrapper>
      </Box>
    </CardBox>
  )
}
